import { Card, Typography, Table, Input, Button, Space, Tooltip, Progress, Tag } from "antd";
import { SearchOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { useState, useMemo } from "react";
import dayjs from "dayjs";

const { Title } = Typography;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Calcula el porcentaje de avance.
 * Si familias_asignadas = 0 → no aplica (encuestas sin promotor asignado).
 */
function calcularPorcentaje(realizadas, asignadas) {
  if (!asignadas || asignadas === 0) return null; // null = "N/A"
  return +((realizadas / asignadas) * 100).toFixed(2);
}

function colorAvance(pct) {
  if (pct === null)  return "#B4B4B4";  // gris — sin asignación
  if (pct >= 100)    return "#52C41A";  // verde — completado
  if (pct >= 75)     return "#024589";  // azul — buen avance
  if (pct >= 50)     return "#F4A000";  // naranja — en progreso
  return               "#D62828";       // rojo — bajo avance
}

function tagAvance(pct) {
  if (pct === null)  return { color: "default",    label: "Sin asignación" };
  if (pct >= 100)    return { color: "success",    label: "Completado"     };
  if (pct >= 75)     return { color: "processing", label: "Avanzado"       };
  if (pct >= 50)     return { color: "warning",    label: "En progreso"    };
  return               { color: "error",           label: "Bajo"           };
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function EncuestadoresChart({ data = [] }) {
  const [busqueda, setBusqueda] = useState("");

  // ⚠️ Hooks siempre antes de cualquier return anticipado
  const dataConPorcentaje = useMemo(
    () =>
      data.map((d) => ({
        ...d,
        porcentaje_avance: calcularPorcentaje(d.encuestas_realizadas, d.familias_asignadas),
        // clave única: si promotor_id es null usamos "sin-promotor"
        _key: d.promotor_id != null ? String(d.promotor_id) : "sin-promotor",
      })),
    [data]
  );

  // Guard: ahora sí puede ir el return anticipado, después de todos los hooks
  if (!data || data.length === 0) {
    return (
      <Card hoverable style={{ borderRadius: 12 }}>
        <Title level={5}>Avance por Encuestador</Title>
        <p>No hay datos disponibles.</p>
      </Card>
    );
  }

  const datosFiltrados = dataConPorcentaje.filter((d) =>
    d.encuestador.toLowerCase().includes(busqueda.toLowerCase())
  );

  // Totales globales (excluye los sin asignación para el % global)
  const totalRealizadas  = dataConPorcentaje.reduce((s, d) => s + d.encuestas_realizadas, 0);
  const totalAsignadas   = dataConPorcentaje.reduce((s, d) => s + d.familias_asignadas,   0);
  const pctGlobal        = calcularPorcentaje(totalRealizadas, totalAsignadas);

  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, i) => (
        <span style={{ color: "#888", fontSize: 12 }}>{i + 1}</span>
      ),
    },
    {
      title: "Encuestador",
      dataIndex: "encuestador",
      key: "encuestador",
      sorter: (a, b) => a.encuestador.localeCompare(b.encuestador),
      render: (text, record) => (
        <span style={{ fontWeight: 500, color: record.promotor_id == null ? "#888" : "inherit" }}>
          {text}
        </span>
      ),
    },
    {
      title: "Familias asignadas",
      dataIndex: "familias_asignadas",
      key: "familias_asignadas",
      width: 160,
      align: "center",
      sorter: (a, b) => a.familias_asignadas - b.familias_asignadas,
      render: (v) => (
        <span style={{ color: "#555" }}>
          {v > 0 ? Number(v).toLocaleString() : <span style={{ color: "#bbb" }}>—</span>}
        </span>
      ),
    },
    {
      title: "Encuestas realizadas",
      dataIndex: "encuestas_realizadas",
      key: "encuestas_realizadas",
      width: 170,
      align: "center",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.encuestas_realizadas - b.encuestas_realizadas,
      render: (v) => (
        <span style={{ fontWeight: 700, color: "#024589" }}>
          {Number(v).toLocaleString()}
        </span>
      ),
    },
    {
      title: "% Avance",
      dataIndex: "porcentaje_avance",
      key: "porcentaje_avance",
      width: 260,
      sorter: (a, b) => {
        // null siempre va al fondo
        if (a.porcentaje_avance === null) return -1;
        if (b.porcentaje_avance === null) return 1;
        return a.porcentaje_avance - b.porcentaje_avance;
      },
      render: (pct) => {
        const { color, label } = tagAvance(pct);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress
              percent={pct !== null ? Math.min(pct, 100) : 0}
              strokeColor={colorAvance(pct)}
              trailColor="#f0f0f0"
              showInfo={false}
              strokeWidth={10}
              style={{ flex: 1, margin: 0 }}
            />
            <span style={{ width: 52, textAlign: "right", fontWeight: 700, fontSize: 12, color: colorAvance(pct) }}>
              {pct !== null ? `${pct}%` : "N/A"}
            </span>
            <Tag color={color} style={{ margin: 0, fontSize: 11 }}>
              {label}
            </Tag>
          </div>
        );
      },
    },
  ];

  const exportXLSX = () => {
    // 1. Filas de datos
    const filas = dataConPorcentaje.map((d, i) => ({
      "#":                    i + 1,
      "Encuestador":          d.encuestador,
      "Familias Asignadas":   d.familias_asignadas,
      "Encuestas Realizadas": d.encuestas_realizadas,
      "% Avance":             d.porcentaje_avance !== null ? d.porcentaje_avance : "N/A",
    }));

    // 2. Fila de totales
    filas.push({
      "#":                    "",
      "Encuestador":          "TOTAL",
      "Familias Asignadas":   totalAsignadas,
      "Encuestas Realizadas": totalRealizadas,
      "% Avance":             pctGlobal !== null ? pctGlobal : "N/A",
    });

    // 3. Crear hoja y libro
    const hoja  = XLSX.utils.json_to_sheet(filas);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Avance Encuestadores");

    // 4. Ajustar ancho de columnas automáticamente
    hoja["!cols"] = [
      { wch: 5  },  // #
      { wch: 35 },  // Encuestador
      { wch: 20 },  // Familias Asignadas
      { wch: 22 },  // Encuestas Realizadas
      { wch: 12 },  // % Avance
    ];

    // 5. Descargar
    XLSX.writeFile(libro, `avance_encuestadores_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  return (
    <Card hoverable style={{ borderRadius: 12 }}>
      {/* Cabecera */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Avance por Encuestador</Title>
          <div style={{ fontSize: 14, color: "#666", marginTop: 4, display: "flex", gap: 20, flexWrap: "wrap" }}>
            <span>{data.length} encuestadores</span>
            <span>
              <b style={{ color: "#024589" }}>{totalRealizadas.toLocaleString()}</b>
              {" / "}
              {totalAsignadas.toLocaleString()} familias
            </span>
            <span>
              Avance global:{" "}
              <b style={{ color: colorAvance(pctGlobal) }}>
                {pctGlobal !== null ? `${pctGlobal}%` : "N/A"}
              </b>
            </span>
          </div>
        </div>
        <Space>
          <Input
            placeholder="Buscar encuestador..."
            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Tooltip title="Exportar Excel">
            <Button icon={<FileExcelOutlined />} onClick={exportXLSX} size="small">
              Excel
            </Button>
          </Tooltip>
        </Space>
      </div>

      {/* Tabla */}
      <Table
        dataSource={datosFiltrados}
        columns={columns}
        rowKey="_key"
        size="small"
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showTotal: (total) => `${total} encuestadores`,
        }}
        rowClassName={(record) =>
          record.promotor_id == null ? "row-sin-promotor" : ""
        }
        summary={() => (
          <Table.Summary.Row style={{ backgroundColor: "#f0f5ff" }}>
            <Table.Summary.Cell index={0} />
            <Table.Summary.Cell index={1}>
              <span style={{ fontWeight: 700 }}>TOTAL</span>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="center">
              <span style={{ color: "#555" }}>{totalAsignadas.toLocaleString()}</span>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="center">
              <span style={{ fontWeight: 700, color: "#024589" }}>{totalRealizadas.toLocaleString()}</span>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Progress
                  percent={pctGlobal !== null ? Math.min(pctGlobal, 100) : 0}
                  strokeColor={colorAvance(pctGlobal)}
                  trailColor="#f0f0f0"
                  showInfo={false}
                  strokeWidth={10}
                  style={{ flex: 1, margin: 0 }}
                />
                <span style={{ fontWeight: 700, color: colorAvance(pctGlobal) }}>
                  {pctGlobal !== null ? `${pctGlobal}%` : "N/A"}
                </span>
              </div>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </Card>
  );
}
